import { useApiClient } from '@/composables/api-client'
import { createQuery } from '@/utils/pinia/helpers'
import { createSimpleStoreWrapper } from '~/utils/pinia/simple-store-setup'
import { useFantasyCalendarStore } from '~/stores/fantasy/calendar'
import { useFantasyDayStore } from '~/stores/fantasy/day'
import type { FantasySeason } from '~/types/fantasy'

const createSimpleStore = createSimpleStoreWrapper<FantasySeason>()

const SEASON_PATH = '/api/v1/fantasy/season'
const SEASON_QUERY = {
  expand: ['rewards.resource', 'memberStatus', 'game']
}

export const useFantasySeasonStore = createSimpleStore(
  'fantasy-season',
  {
    path: SEASON_PATH,
    query: SEASON_QUERY
  },
  context => {
    const fetchData = async (id?: number) => {
      const apiClient = await useApiClient()
      const query = createQuery({ path: SEASON_PATH }, SEASON_QUERY)
      const url = id ? `${SEASON_PATH}/${id}` : SEASON_PATH

      context.data.value = await apiClient.get<FantasySeason>(url, { query })

      return context.data.value
    }

    const isCompleted = computed(() =>
      Boolean(context.data.value?.is_completed)
    )

    const canClaimFreeCards = computed(() =>
      Boolean(context.data.value?.can_claim_free_cards)
    )

    const freeCards = computed(
      () => context.data.value?.free_card ?? { types: {}, grades: {} }
    )

    const claimSeasonReward = async () => {
      const apiClient = await useApiClient()
      await apiClient.post('/api/v1/fantasy/season/claim-rewards')
      context.updateData({ can_claim: false })
    }

    const claimDayReward = async (id: number | null) => {
      if (id === null) return

      const apiClient = await useApiClient()
      const fantasyCalendarStore = useFantasyCalendarStore()
      const fantasyDayStore = useFantasyDayStore()

      await apiClient.post(`/api/v1/fantasy/season/days/${id}/claim-rewards`)

      if (fantasyDayStore.data?.memberStatus) {
        const memberStatus = Object.assign(
          {},
          fantasyDayStore.data.memberStatus,
          {
            rewarded_at: Math.floor(Date.now() / 1000)
          }
        )
        const updatedData = { can_claim: false, memberStatus }
        fantasyCalendarStore.updateItem(id, updatedData)
        fantasyDayStore.updateData(updatedData)
      }
    }

    return {
      fetchData,
      isCompleted,
      canClaimFreeCards,
      freeCards,
      claimSeasonReward,
      claimDayReward
    }
  }
)
